import * as React from 'react';
import { Panel, Alert, DatePicker, alertColors } from '@ixicare/ui';
import { t } from '~/i18n';
import { formatDateTimeWritten } from '@ixicare/ui';
import MapWithInfo from '~/components/MapWithInfo';
import { observer } from 'mobx-react';
import { useStores } from '~store/utils';
import { useGPSGeofence, useLocation } from '~hooks/location';
import { parseDate } from '~utils/date';
import MapPosition from '~containers/Map/MapPosition';
import LocationTimelineMap from '~containers/Map/LocationTimelineMap';
import { useTimelineWindow } from '~components/LocationTimeline/useTimelineWindow';
import { TimelineBand } from '~components/LocationTimeline/timeline.utils';
import styles from '~components/LocationTimeline/LocationTimeline.module.css';

const DEFAULT_SPAN = 4 * 60 * 60 * 1000;

const ResidentLocationMap: React.FC = () => {
  const { residentDetail } = useStores();
  const device = residentDetail.profile?.ownedDevice;

  if (!device) {
    return (
      <Panel.Section>
        <Panel.Title>{t('residentDetail.location.currentLocation')}</Panel.Title>
        <Alert type="message">{t('residentDetail.location.noLocation')}</Alert>
      </Panel.Section>
    );
  }
  // A static device never moves, so there is no route to replay.
  if (device.locationIsStatic) return <StaticLocationMap />;
  return <ResidentLocationTimeline />;
};

const StaticLocationMap = observer(() => {
  const { residentDetail } = useStores();
  const device = residentDetail.profile?.ownedDevice;
  const locationData = useLocation(device?.id, undefined, true);
  const geo = useGPSGeofence(residentDetail.profile?.id);

  return (
    <Panel.Section>
      <Panel.Title extraInfo={locationData?.timestamp ? formatDateTimeWritten(parseDate(locationData.timestamp)) : ''}>
        {t('residentDetail.location.currentLocation')}
      </Panel.Title>
      <MapWithInfo info={locationData?.description}>
        <MapPosition
          isStatic
          locationData={locationData}
          siteUuid={residentDetail.profile?.site?.uuid}
          geofence={geo}
        />
      </MapWithInfo>
    </Panel.Section>
  );
});

const ResidentLocationTimeline = observer(() => {
  const { residentDetail } = useStores();
  const profile = residentDetail.profile;
  const timeline = useTimelineWindow({ live: true, span: DEFAULT_SPAN });
  const geo = useGPSGeofence(profile?.id);

  React.useEffect(() => {
    residentDetail.loadAlerts();
  }, [profile?.uuid]);

  const bands: TimelineBand[] = residentDetail.alerts.map((alert) => ({
    start: alert.created.getTime(),
    end: (alert.resolvedTime ?? alert.cancelledTime)?.getTime() ?? timeline.now,
    color: alertColors[alert.priority],
  }));

  return (
    <Panel.Section>
      <Panel.Title
        extraInfo={
          <div className={styles.DayPicker}>
            <DatePicker
              mode="single"
              selected={new Date(timeline.end)}
              onSelect={(day) => day && timeline.showDay(day)}
              hidden={{ after: new Date() }}
            />
          </div>
        }
      >
        {t(timeline.live ? 'residentDetail.location.currentLocation' : 'residentDetail.tabs.location')}
      </Panel.Title>
      <LocationTimelineMap
        deviceId={profile!.ownedDevice!.id}
        timeline={timeline}
        siteUuid={profile?.site?.uuid}
        geofence={geo}
        bands={bands}
      />
    </Panel.Section>
  );
});

export default observer(ResidentLocationMap);
